/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { ListItemAvatar, ListItemText } from '@material-ui/core';

import CustomIcons from '../../../icons/CustomIcons';

import * as Utils from '../../../data/utils';
import * as Constants from '../../../data/constants';
import { ApiReducer, ApplicationState, Document, DocumentsReducer, UserReducer } from '../../../data/interfaces';
import { PATH_SEPARATOR } from "../../../data/constants";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    apiDocsTitle: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiDocumentsTitleBox
    },
    apiInfoTitleHead: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiDocumentsTitleText,
    },
    docsList: {
        minWidth: '320px',
        overflowWrap: 'break-word',
        textAlign: 'left',
    },
    listItemText: {
        ...theme.custom.apiOverviewPage.tabs.overview.apiDocumentPropertyText,
    },
}));

interface ApiDocumentationProps {
    organizationHandle: string,
    apiReducer: ApiReducer,
    documentsReducer: DocumentsReducer,
}

function ApiDocumentation({ organizationHandle, apiReducer, documentsReducer}: ApiDocumentationProps) {
    const intl = useIntl();
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { api } = apiReducer;
    const { loading: docsLoading, documents, error: documentsError } = documentsReducer;
    const history = useHistory();
    let docsList: Document[] = [];

    if (Utils.isValidData(documents)) {
        docsList = documents.list;
    }

    useEffect(() => {
        if (documentsError) {
            enqueueSnackbar("Error while retrieving documents", { variant: 'error' })
        }
    }, [documentsReducer]);

    const handleDocItemClick = (documentId: string) => {
        if (api.id && documentId) {
            history.push(PATH_SEPARATOR + organizationHandle + Constants.RESOURCE_API + "/"
                + api.id + Constants.RESOURCE_API_DOCUMENTS + "/" + documentId);
        }
    }

    return (
        <div className={classes.root}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm container className={classes.apiDocsTitle}>
                    <Grid item xs lg={12}>
                        <Typography className={classes.apiInfoTitleHead}>
                            <FormattedMessage
                                id="api.apiOverview.apiDocumentation.labels.documentation"
                                defaultMessage="Documentation"
                            />
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm container lg={12}>
                    {
                        docsLoading ?
                            Utils.renderSkeleton("rect", "100%", 48)
                            :
                            <List className={classes.docsList}>
                                {
                                    docsList.length === 0 ?
                                        <i>
                                            <FormattedMessage
                                                id="api.apiOverview.apiDocumentation.messages.noDocumentsAvailable"
                                                defaultMessage="No documents available"
                                            />
                                        </i>
                                        :
                                        docsList.map((document) => (
                                            <ListItem
                                                key={document.documentId}
                                                button
                                                onClick={() => handleDocItemClick(document.documentId)}
                                            >
                                                <ListItemAvatar>
                                                    <CustomIcons icon={document.type} />
                                                </ListItemAvatar>
                                                <ListItemText
                                                    classes={{ primary: classes.listItemText, secondary: classes.listItemText }}
                                                    primary={document.name} secondary={document.sourceType}
                                                />
                                            </ListItem>
                                        ))}
                            </List>
                    }
                </Grid>
            </Grid>
        </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        documentsReducer: state.documentsReducer
    }
}

export default connect(mapStateToProps)(ApiDocumentation);
